import {
  CloseOutlined,
  EditOutlined,
  LoadingOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Image,
  Input,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  Upload
} from 'antd';
import { REPORT_LANGUAGES } from 'constants/reports';
import dayjs from 'dayjs';
import { fetchAppointmentById } from 'services/api/appointments';
import {
  useReportValuesDispatchContext,
  useReportValuesStateContext
} from 'providers/ReportValues';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SUPPORTED_AUDIO_FORMATS } from 'constants/file';

import { CKEditor, VoiceRecorder } from 'components/shared';
import Signature from 'components/shared/Signature';
import {
  SelectedTag,
  StyledContainer,
  StyledFormItem,
  StyledLoading
} from './styled';
import SearchIcd from '../SearchIcd';
import { Loading } from 'components/ui';
import {
  handleBeforeUpload,
  onFinishRecording,
  uploadAudio,
  validateFileSize
} from '../_utils';

const SUPPORTED_AUDIO_MIME_TYPES = Object.keys(SUPPORTED_AUDIO_FORMATS);

const MAX_FILE_SIZE = 10;

interface IProps {
  loading?: boolean;
  signature: string;
  appointmentId?: string;
  onClickNext: () => void;
  onSignChange: (image: string) => void;
  onSaveDraft: () => void;
}

const PatientInfo: FC<IProps> = ({
  loading,
  signature,
  appointmentId,
  onClickNext,
  onSignChange,
  onSaveDraft
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const [form] = Form.useForm();
  const { setValues } = useReportValuesDispatchContext();
  const values = useReportValuesStateContext();
  const lang = Form.useWatch('lang', form);
  const [error, setError] = useState(false);
  const [editSignature, setEditSignature] = useState(false);
  const canceledRecordingRef = useRef<boolean>(false);
  const [isCreatingTranscript, setCreatingTranscript] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [patient, setPatient] = useState<any>(null);

  useEffect(() => {
    // prefill from the appointment's patient snapshot when adding a report
    // for an appointment (not when editing an existing report)
    if (!appointmentId || params.id) return;

    fetchAppointmentById(appointmentId)
      .then(appointment => setPatient(appointment?.patient ?? null))
      .catch(() => setPatient(null));
  }, [appointmentId, params.id]);

  const removeIcd = (value: string) => {
    setValues({ ...values, icd: values.icd.filter(item => item.id !== value) });
  };

  const customRequest = onSuccess => {
    setTimeout(() => {
      onSuccess();
    }, 0);
  };

  const onSignatureEnd = useCallback(
    (image: string) => {
      onSignChange(image);
      setError(false);
    },
    [onSignChange]
  );

  const onClear = useCallback(() => {
    onSignChange('');
  }, [onSignChange]);

  const handleBeforeUploadFile = (file: File) =>
    handleBeforeUpload(file, onSignChange, setError);

  const handleUpload = (file: File) => {
    uploadAudio(file, lang, setValues, form, values, setCreatingTranscript);
  };

  const handleFinishRecording = useCallback(
    async (url: string) => {
      await onFinishRecording(
        url,
        lang,
        setValues,
        form,
        values,
        canceledRecordingRef.current,
        setCreatingTranscript
      );
    },
    [form, lang, setValues, values]
  );

  const handleCancelRecording = () => {
    canceledRecordingRef.current = true;
  };

  const resetCancelFlag = () => {
    canceledRecordingRef.current = false;
  };

  const onClickDraft = useCallback(async () => {
    try {
      await form.validateFields();
      onSaveDraft();
    } catch {
      //
    }
  }, [form, onSaveDraft]);

  const onFinish = () => {
    if (signature) {
      return onClickNext();
    }

    setError(true);
  };

  const renderAudioRow = () => (
    <Flex
      gap={12}
      style={{ marginTop: isCreatingTranscript ? 48 : 10 }}
      justify="flex-end"
    >
      <Upload
        accept={SUPPORTED_AUDIO_MIME_TYPES.join(',')}
        showUploadList={false}
        customRequest={({ file }) => handleUpload(file as File)}
        beforeUpload={file => validateFileSize(file, MAX_FILE_SIZE)}
      >
        <Button
          disabled={!lang || isCreatingTranscript}
          icon={<UploadOutlined style={{ fontSize: 18 }} />}
        >
          {t('reports.import_audio')}
        </Button>
      </Upload>
      <VoiceRecorder
        disabled={!lang || isCreatingTranscript}
        onFinish={handleFinishRecording}
        onCancel={handleCancelRecording}
        resetCancelFlag={resetCancelFlag}
      />
    </Flex>
  );

  if (appointmentId && !params.id && !patient) {
    return <Loading />;
  }

  return (
    <StyledContainer>
      <Form
        form={form}
        layout="vertical"
        size="large"
        onValuesChange={(_, value) => {
          setValues({
            ...value,
            icd: values.icd
          });
        }}
        initialValues={{
          date_of_birth:
            values.date_of_birth || patient?.date_of_birth
              ? dayjs(patient?.date_of_birth)
              : null,
          report_date: values.report_date,
          first_name: values.first_name || patient?.first_name || '',
          last_name: values.last_name || patient?.last_name || '',
          report: values.report,
          email: values.email,
          lang: values.lang,
          icd: values.icd
        }}
        onFinish={onFinish}
        scrollToFirstError
      >
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <div>
              <StyledFormItem
                label={t('reports.report_language')}
                name="lang"
                rules={[{ required: true }]}
              >
                <Select placeholder={t('reports.report_language')}>
                  {REPORT_LANGUAGES.map(item => (
                    <Select.Option key={item.key} value={item.key}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </StyledFormItem>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <StyledFormItem
                label={t('reports.report_date')}
                name="report_date"
                rules={[{ required: true }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder={t('reports.date_picker_placeholder')}
                  disabledDate={current => dayjs().diff(current, 'minutes') < 0}
                />
              </StyledFormItem>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <StyledFormItem
                label={t('reports.patient_first_name')}
                name="first_name"
                rules={[{ required: true }]}
              >
                <Input placeholder={t('reports.patient_first_name')} />
              </StyledFormItem>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <StyledFormItem
                label={t('reports.patient_last_name')}
                name="last_name"
                rules={[{ required: true }]}
              >
                <Input placeholder={t('reports.last_name')} />
              </StyledFormItem>
            </div>
          </Col>
          {!appointmentId && (
            <Col span={12}>
              <div data-element="report-email-input">
                <div>
                  <StyledFormItem
                    label={t('reports.email')}
                    name="email"
                    rules={[{ required: true }, { type: 'email' }]}
                  >
                    <Input placeholder="email@example.com" />
                  </StyledFormItem>
                </div>
              </div>
            </Col>
          )}
          <Col span={12}>
            <div>
              <StyledFormItem
                label={t('reports.date_of_birth')}
                name="date_of_birth"
                rules={[{ required: true }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder={t('reports.date_picker_placeholder')}
                  disabledDate={current => dayjs().diff(current, 'minutes') < 0}
                />
              </StyledFormItem>
            </div>
          </Col>
          <Col span={24}>
            <div>
              <div style={{ position: 'relative' }}>
                <StyledFormItem
                  label={t('reports.report')}
                  name="report"
                  rules={[{ required: true }]}
                >
                  <CKEditor
                    disabled={isCreatingTranscript}
                    toolbar={{
                      removeItems: [
                        'link',
                        'blockQuote',
                        'insertTable',
                        'mediaEmbed'
                      ]
                    }}
                  />
                </StyledFormItem>
                {isCreatingTranscript && (
                  <StyledLoading gap={8}>
                    <div style={{ paddingTop: 4 }}>
                      <LoadingOutlined
                        style={{ fontSize: 24, color: '#2552F3' }}
                      />
                    </div>
                    <Typography.Text
                      type="secondary"
                      style={{
                        color: '#2552F3',
                        marginTop: 8,
                        display: 'block',
                        textAlign: 'right',
                        fontSize: 14,
                        userSelect: 'none'
                      }}
                    >
                      {t('reports.creating_transcript')}
                    </Typography.Text>
                  </StyledLoading>
                )}
              </div>
              {lang ? (
                renderAudioRow()
              ) : (
                <Tooltip
                  title={t('reports.select_language_message')}
                  placement="topRight"
                >
                  {renderAudioRow()}
                </Tooltip>
              )}
            </div>
          </Col>
          <Col span={24}>
            <div
              style={{
                width: '100%'
              }}
            >
              <StyledFormItem
                label={t('reports.report_icd')}
                name="icd"
                style={{ marginBottom: 10 }}
              >
                <SearchIcd />
              </StyledFormItem>
            </div>
          </Col>
          <Col span={24}>
            <Flex gap={8} wrap="wrap">
              {values.icd.map(item => (
                <SelectedTag key={item.id + item.title}>
                  <Typography.Text
                    style={{ width: '100%', lineHeight: '23px' }}
                  >
                    {item.title}
                  </Typography.Text>
                  <CloseOutlined
                    style={{ fontSize: 10 }}
                    onClick={() => removeIcd(item.id)}
                  />
                </SelectedTag>
              ))}
            </Flex>
          </Col>
          <Col span={24} style={{ marginTop: 24 }}>
            <Row justify="space-between" align="middle">
              {t('profile.signature')}
              {signature ? (
                <Button onClick={() => setEditSignature(true)}>
                  <EditOutlined /> {t('reports.edit_signature')}
                </Button>
              ) : (
                <div style={{ backgroundColor: 'red' }}>
                  <Upload
                    data-element="upload-signature-button"
                    multiple={false}
                    maxCount={1}
                    beforeUpload={handleBeforeUploadFile}
                    customRequest={({ onSuccess }) => customRequest(onSuccess)}
                    showUploadList={false}
                    name="file"
                    accept="image/png"
                  >
                    <Button icon={<UploadOutlined />}>
                      {t('profile.upload')}
                    </Button>
                  </Upload>
                </div>
              )}
            </Row>
            <div>
              {!editSignature && !!signature ? (
                <Image src={signature} preview={false} />
              ) : (
                <Signature onClear={onClear} onEnd={onSignatureEnd} />
              )}
            </div>
            {error && (
              <Typography.Text type="danger">
                {t('profile.signature_required')}
              </Typography.Text>
            )}
          </Col>
        </Row>
        <Space
          style={{
            paddingTop: 24,
            justifyContent: 'flex-end',
            width: '100%'
          }}
        >
          <Button ghost type="primary" loading={loading} onClick={onClickDraft}>
            {t('reports.save')}
          </Button>
          <Button onClick={() => form.submit()} type="primary">
            {t('reports.next')}
          </Button>
        </Space>
      </Form>
    </StyledContainer>
  );
};

export default PatientInfo;
